import React from 'react';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '~/components/ui/breadcrumb';
import { cn } from '~/lib/utils';

interface BreadcrumbEntry {
  label?: string;
  href?: string;
  content?: React.ReactNode;
}

interface PageContainerProps {
  children: React.ReactNode;
  title?: string;
  badge?: React.ReactNode;
  subtitle?: string;
  actions?: React.ReactNode;
  breadcrumbs?: BreadcrumbEntry[];
  fullWidth?: boolean;
  noScroll?: boolean;
  className?: string;
}

export function PageContainer({
  children,
  title,
  badge,
  subtitle,
  actions,
  breadcrumbs,
  fullWidth,
  noScroll,
  className
}: PageContainerProps): React.ReactNode {
  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;

  return (
    <div className={cn('flex h-full flex-col', className)}>
      <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-xl border-b border-border/50 bg-card px-4 py-2">
        <div>
          {hasBreadcrumbs && (
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((item, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <BreadcrumbSeparator />}
                    {item.content ? (
                      item.content
                    ) : (
                      <BreadcrumbItem>
                        {item.href ? (
                          <BreadcrumbLink
                            href={item.href}
                            className="block max-w-[3rem] sm:max-w-[12rem] truncate"
                          >
                            {item.label}
                          </BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage className="block max-w-[3rem] sm:max-w-[12rem] truncate">
                            {item.label}
                          </BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                    )}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          )}
        </div>
      </div>
      <div
        className={cn(
          'flex flex-1 min-h-0 flex-col gap-6 p-4 sm:p-8',
          noScroll ? 'overflow-hidden' : 'overflow-y-auto',
          !fullWidth && 'mx-auto w-full max-w-7xl'
        )}
      >
        {(title ?? subtitle) && (
          <div
            className={cn(
              'flex flex-col gap-1',
              actions && 'flex-row items-start justify-between gap-4'
            )}
          >
            <div className="flex min-w-0 flex-col gap-1">
              {title && (
                <div className="flex min-w-0 items-center gap-2">
                  <h1 className="min-w-0 truncate text-2xl font-semibold tracking-tight">
                    {title}
                  </h1>
                  {badge}
                </div>
              )}
              {subtitle && <p className="truncate text-sm text-muted-foreground">{subtitle}</p>}
            </div>
            {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
